import {Component, ErrorInfo, ReactNode} from 'react';

interface AppErrorBoundaryProps {
    children: ReactNode;
}

interface AppErrorBoundaryState {
    error: Error | null;
}

/**
 * The last thing between a crash while rendering and a blank page.
 *
 * There was no boundary anywhere in the tree, so a throw during the first render took the whole
 * application down and left white - no message, no way back, nothing to report. That is how a single
 * unguarded `Object.keys` in the route table read to the person in front of it as "the site is broken
 * in Safari".
 *
 * Deliberately built from nothing: no store, no theme, no translations, no shared components. Whatever
 * failed a moment ago may be exactly those, and a fallback that depends on them fails with them.
 * English only, for the same reason - reaching react-intl means reaching a provider that may be the
 * thing that threw.
 *
 * The second button is the important one. Almost every crash of this kind comes from persisted state a
 * newer build cannot read, and clearing it is what a developer would tell them to do through five
 * levels of browser settings. Here it is one click, and it does not touch anything but this origin.
 */
export class AppErrorBoundary extends Component<AppErrorBoundaryProps, AppErrorBoundaryState> {
    state: AppErrorBoundaryState = {error: null};

    static getDerivedStateFromError(error: Error): AppErrorBoundaryState {
        return {error};
    }

    componentDidCatch(error: Error, info: ErrorInfo) {
        // Kept: the browser console is the only report we get from a user who cannot open a screen.
        console.error('Unhandled error while rendering', error, info.componentStack);
    }

    private reload = () => window.location.reload();

    private resetAndReload = () => {
        try {
            window.localStorage.clear();
            window.sessionStorage.clear();
        } catch {
            // Storage can be unavailable outright - a private window with cookies blocked, say. The
            // reload is still worth attempting, and it is what the other button does anyway.
        }
        window.location.reload();
    };

    render() {
        if (!this.state.error) {
            return this.props.children;
        }

        return (
            <div style={wrapper}>
                <div style={card}>
                    <h1 style={title}>Something went wrong</h1>
                    <p style={text}>
                        The page could not be displayed. Reloading usually helps; if it does not, clear
                        the data this site keeps in your browser and sign in again.
                    </p>
                    <div style={actions}>
                        <button
                            type="button"
                            style={primaryButton}
                            onClick={this.reload}
                        >
                            Reload
                        </button>
                        <button
                            type="button"
                            style={secondaryButton}
                            onClick={this.resetAndReload}
                        >
                            Clear data and sign in again
                        </button>
                    </div>
                    <pre style={details}>{this.state.error.message}</pre>
                </div>
            </div>
        );
    }
}

const wrapper: React.CSSProperties = {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 24,
    background: '#F4F6FA',
    fontFamily: 'system-ui, -apple-system, "Segoe UI", Roboto, sans-serif',
};

const card: React.CSSProperties = {
    maxWidth: 520,
    width: '100%',
    background: '#FFFFFF',
    borderRadius: 14,
    border: '1px solid rgba(0, 0, 0, 0.05)',
    boxShadow: '0 1px 3px rgba(0,0,0,0.05), 0 4px 12px rgba(0,0,0,0.04)',
    padding: 32,
};

const title: React.CSSProperties = {
    margin: '0 0 12px',
    fontSize: 20,
    fontWeight: 600,
    color: '#1B1B28',
};

const text: React.CSSProperties = {
    margin: '0 0 24px',
    fontSize: 14,
    lineHeight: 1.5,
    color: '#5A5A6E',
};

const actions: React.CSSProperties = {
    display: 'flex',
    gap: 12,
    flexWrap: 'wrap',
};

const buttonBase: React.CSSProperties = {
    padding: '10px 18px',
    borderRadius: 8,
    fontSize: 14,
    cursor: 'pointer',
    fontFamily: 'inherit',
};

const primaryButton: React.CSSProperties = {
    ...buttonBase,
    border: 'none',
    background: '#2E5BFF',
    color: '#FFFFFF',
};

const secondaryButton: React.CSSProperties = {
    ...buttonBase,
    border: '1px solid rgba(0, 0, 0, 0.15)',
    background: 'transparent',
    color: '#1B1B28',
};

const details: React.CSSProperties = {
    margin: '24px 0 0',
    fontSize: 12,
    lineHeight: 1.4,
    color: '#8A8A9E',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
};
